import { useState } from 'react';
import type { DirectoryResponse, EmployeeIdentityLinkSummaryResponse, EmployeeIdentityLinksResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, Field, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Employee Identity Provisioning — the admin surface for
 * `api/src/routes/employeeIdentity.ts` (`docs/API.md` §5.21a).
 *
 * ---------------------------------------------------------------------
 * WHAT THIS SECTION IS, AND WHAT IT DELIBERATELY IS NOT
 *
 * This links an `Employee` record to the local-auth account id
 * (Better Auth's own internal `"user"."id"` —
 * `docs/AUTHENTICATION_REASSESSMENT.md`) that production authentication
 * will match a signed-in session against. It is not an HR system and not
 * an account directory: there is no directory browse, no "look up this
 * account" button and no automatic matching by email or name. The
 * administrator must already know the exact account id (from the
 * bootstrap procedure, `docs/AUTHENTICATION.md`, or a future
 * self-service sign-up flow) — the same deliberate narrowness
 * `employeeIdentityService.ts` enforces server-side
 * (`docs/DOMAIN_DECISIONS.md`, "Production Employee Identity Mapping
 * Architecture").
 *
 * Employee selection reuses the existing directory read (`GET
 * /employees`, the same data `DirectoryPage` lists) rather than building
 * a second search surface — this dataset is small enough that a plain
 * list is still the right answer here, the same call the roadmap gap
 * analysis made for the Directory itself.
 *
 * Correct and Revoke are two distinct, separately-confirmed actions, not
 * one "edit" — mirroring every other lifecycle control in this admin
 * area (see `adminShared.tsx`'s own note on this). Correcting never edits
 * the existing row: it inserts a new one and the previous link stays
 * visible here as history, which the correction form says explicitly
 * before it is submitted.
 * ---------------------------------------------------------------------
 */

const PROVIDER_LABEL: Record<string, string> = { local: 'EmployeeProfile (local account)' };

function providerLabel(provider: string): string {
  return PROVIDER_LABEL[provider] ?? provider;
}

function formatLinkedAt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function EmployeeIdentitySection() {
  const links = useApiResource<EmployeeIdentityLinksResponse>('/admin/employee-identities');
  const directory = useApiResource<DirectoryResponse>('/employees');
  const [creating, setCreating] = useState(false);
  const [correctingId, setCorrectingId] = useState<string | null>(null);

  const loading = links.status === 'loading' || directory.status === 'loading';
  if (loading) return <LoadingState label="Loading employee identity links…" />;
  if (links.status === 'failed') {
    return <ErrorState title="Employee identity links could not be loaded" detail={describeError(links.error)} />;
  }
  if (directory.status === 'failed') {
    return <ErrorState title="The employee list could not be loaded" detail={describeError(directory.error)} />;
  }

  const employees = directory.data.employees;
  const reloadAll = () => {
    links.reload();
  };

  return (
    <section className={styles.section} aria-label="Employee identity">
      <h2 className={styles.sectionTitle}>Employee identity</h2>
      <p className={styles.sectionNote}>
        Which external sign-in account authenticates as which employee. Production sign-in matches the signed-in
        account's subject identifier against these links — an employee with no link here cannot sign in through
        production authentication. Nothing here is deleted: a link is corrected (replaced, with the previous one kept
        as history) or revoked (kept, marked no longer valid).
      </p>

      <ul className={styles.list}>
        {links.data.links.length === 0 ? <li className={styles.rowMeta}>No employee identity links exist yet.</li> : null}
        {links.data.links.map((link) => (
          <li key={link.id} className={styles.row}>
            <span className={styles.rowName}>{link.employeeFullName}</span>
            <span className={styles.rowMeta}>{providerLabel(link.provider)}</span>
            <span className={styles.rowMeta}>{link.subjectClaim}</span>
            <span className={styles.statusTag}>{link.status === 'revoked' ? 'revoked' : 'active'}</span>
            <span className={styles.rowMeta}>Linked {formatLinkedAt(link.createdAt)}</span>
            {link.status === 'active' ? (
              <span className={styles.rowActions}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setCorrectingId(correctingId === link.id ? null : link.id)}
                >
                  {correctingId === link.id ? 'Close' : 'Correct'}
                </button>
              </span>
            ) : null}
            {link.status === 'active' ? (
              <RevokeAction link={link} onDone={reloadAll} />
            ) : link.revocationReason ? (
              <span className={styles.rowMeta}>Revoked: {link.revocationReason}</span>
            ) : null}
            {correctingId === link.id ? (
              <IdentityLinkForm
                mode="correct"
                supersedesId={link.id}
                employees={employees}
                initialEmployeeId={link.employeeId}
                initialSubjectClaim={link.subjectClaim}
                onDone={() => {
                  setCorrectingId(null);
                  reloadAll();
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>

      {creating ? (
        <IdentityLinkForm
          mode="create"
          employees={employees}
          onDone={() => {
            setCreating(false);
            reloadAll();
          }}
        />
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
            Link an identity
          </button>
        </div>
      )}
    </section>
  );
}

function RevokeAction({ link, onDone }: { link: EmployeeIdentityLinkSummaryResponse; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const { state, run } = useAction();

  async function revoke() {
    const ok = await run(async () => {
      await apiPost(`/admin/employee-identities/${link.id}/revocation`, { reason });
      return `The identity link for ${link.employeeFullName} was revoked.`;
    });
    if (ok) onDone();
  }

  if (!open) {
    return (
      <span className={styles.rowActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => setOpen(true)}>
          Revoke
        </button>
      </span>
    );
  }

  return (
    <div className={styles.confirm}>
      <p className={styles.confirmText}>
        Revoking this link means <strong>{link.employeeFullName}</strong> can no longer sign in through this{' '}
        {providerLabel(link.provider)} account. This does not delete the record — it stays listed here, marked
        revoked, and the same external account may later be linked to someone else.
      </p>
      <Field id={`revoke-reason-${link.id}`} label="Reason" value={reason} onChange={setReason} multiline hint="Required." />
      <ActionFeedback state={state} />
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.dangerButton}
          onClick={revoke}
          disabled={state.status === 'saving' || reason.trim().length === 0}
        >
          {state.status === 'saving' ? 'Working…' : 'Revoke this link'}
        </button>
        <button type="button" className={styles.secondaryButton} onClick={() => setOpen(false)} disabled={state.status === 'saving'}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function IdentityLinkForm({
  mode,
  supersedesId,
  employees,
  initialEmployeeId,
  initialSubjectClaim,
  onDone,
}: {
  mode: 'create' | 'correct';
  supersedesId?: string;
  employees: DirectoryResponse['employees'];
  initialEmployeeId?: string;
  initialSubjectClaim?: string;
  onDone: () => void;
}) {
  const [employeeId, setEmployeeId] = useState(initialEmployeeId ?? employees[0]?.id ?? '');
  const [subjectClaim, setSubjectClaim] = useState(initialSubjectClaim ?? '');
  const { state, run } = useAction();
  const fieldPrefix = mode === 'create' ? 'new-identity' : `correct-identity-${supersedesId}`;

  async function submit() {
    const ok = await run(async () => {
      const path = mode === 'create' ? '/admin/employee-identities' : `/admin/employee-identities/${supersedesId}/correction`;
      await apiPost(path, { employeeId, provider: 'local', subjectClaim: subjectClaim.trim() });
      return mode === 'create' ? 'The identity was linked.' : 'The correction was recorded. The previous link is kept as history.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm
      title={mode === 'create' ? 'Link an identity' : 'Correct this identity link'}
      onSubmit={submit}
      submitLabel={mode === 'create' ? 'Link identity' : 'Save correction'}
      busy={state.status === 'saving'}
    >
      {mode === 'correct' ? (
        <p className={styles.hint}>
          This creates a new link and keeps the current one as history — nothing is edited in place.
        </p>
      ) : null}

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${fieldPrefix}-employee`}>
          Employee
        </label>
        <select
          id={`${fieldPrefix}-employee`}
          className={styles.input}
          value={employeeId}
          onChange={(event) => setEmployeeId(event.target.value)}
        >
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.fullName}
              {employee.employmentStatus !== 'active' ? ` (${employee.employmentStatus})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${fieldPrefix}-provider`}>
          Provider
        </label>
        <input id={`${fieldPrefix}-provider`} className={styles.input} type="text" value={providerLabel('local')} disabled />
      </div>

      <Field
        id={`${fieldPrefix}-subject`}
        label="Subject identifier (sub)"
        value={subjectClaim}
        onChange={setSubjectClaim}
        hint="The verified subject claim from the identity provider — not an email address, username or employee number."
      />

      <ActionFeedback state={state} />
    </AdminForm>
  );
}
